"use client";

import { useEffect, useCallback } from "react";
import { useAnnotationStore } from "./store";
import { engine } from "./engine";
import type { Annotation, AnnotationStyle, AnnotationColor } from "./types";
import { COLORS } from "./types";

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export default function Annotator() {
  const {
    annotations,
    isEnabled,
    activeStyle,
    activeColor,
    addAnnotation,
    removeAnnotation,
    setEnabled,
    setActiveStyle,
    setActiveColor,
    loadAnnotations,
    clearAll,
  } = useAnnotationStore();

  useEffect(() => {
    // Clean up any malformed annotations from previous versions
    engine.cleanupMalformed();

    const pageUrl = window.location.href;
    loadAnnotations(pageUrl);
  }, [loadAnnotations]);

  useEffect(() => {
    engine.removeAll();
    annotations.forEach((annotation) => {
      engine.render(annotation);
    });
  }, [annotations]);

  const handleSelection = useCallback(async () => {
    if (!isEnabled) return;

    const target = engine.createFromSelection();
    if (!target) return;

    const annotation: Annotation = {
      id: generateId(),
      style: activeStyle,
      color: activeColor,
      target,
      created: Date.now(),
      updated: Date.now(),
      pageUrl: window.location.href,
    };

    await addAnnotation(annotation);
    window.getSelection()?.removeAllRanges();
  }, [isEnabled, activeStyle, activeColor, addAnnotation]);

  useEffect(() => {
    if (!isEnabled) return;

    const handleMouseUp = () => {
      setTimeout(handleSelection, 10);
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [isEnabled, handleSelection]);

  const handleRemove = useCallback(
    async (id: string) => {
      engine.remove(id);
      await removeAnnotation(id);
    },
    [removeAnnotation]
  );

  const handleClearAll = useCallback(async () => {
    if (!confirm("Remove all annotations from this page?")) return;
    engine.removeAll();
    await clearAll();
  }, [clearAll]);

  const annotationsList = Array.from(annotations.values()).sort(
    (a, b) => b.created - a.created
  );

  return (
    <div className="flex h-full w-full flex-col gap-3">
      {/* Enable Toggle */}
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 accent-[--color-accent]"
        />
        <span className="text-sm font-medium">Enable Annotations</span>
      </label>

      {/* Style & Color Controls */}
      <div className="flex flex-col gap-3 p-3 rounded-md border border-[--color-border] bg-card">
        {/* Style Selector */}
        <div>
          <div className="text-xs font-medium text-[color:var(--color-muted-foreground)] mb-2">
            STYLE
          </div>
          <div className="flex gap-2">
            {(["highlight", "underline"] as AnnotationStyle[]).map((style) => (
              <button
                key={style}
                onClick={() => setActiveStyle(style)}
                className={`flex-1 h-9 rounded-md text-sm font-medium transition-all ${
                  activeStyle === style
                    ? "bg-[--color-accent] text-[--color-accent-foreground] shadow-lg ring-2 ring-[--color-accent] ring-offset-2"
                    : "bg-[--surface] hover:bg-[--surface]/80"
                }`}
              >
                {style.charAt(0).toUpperCase() + style.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Color Selector */}
        <div>
          <div className="text-xs font-medium text-[color:var(--color-muted-foreground)] mb-2">
            COLOR
          </div>
          <div className="grid grid-cols-4 gap-2">
            {(Object.keys(COLORS) as AnnotationColor[]).map((color) => {
              const config = COLORS[color];
              const isSelected = activeColor === color;
              return (
                <button
                  key={color}
                  onClick={() => setActiveColor(color)}
                  className={`h-10 rounded-md transition-all relative overflow-hidden ${
                    isSelected
                      ? "ring-[3px] ring-[--color-accent] ring-offset-2 shadow-lg scale-105"
                      : "ring-1 ring-[--color-border] hover:scale-105 hover:shadow-md"
                  }`}
                  title={config.name}
                  aria-label={`Select ${config.name}`}
                  style={{
                    backgroundColor:
                      activeStyle === "highlight"
                        ? config.highlight
                        : "#f5f5f5",
                    borderBottom:
                      activeStyle === "underline"
                        ? `3px solid ${config.underline}`
                        : undefined,
                  }}
                >
                  {isSelected && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-[--color-accent] drop-shadow-lg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Annotations List */}
      <div className="flex-1 flex flex-col overflow-hidden rounded-md border border-[--color-border] bg-card">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[--color-border]">
          <div className="text-sm font-medium">
            Annotations ({annotationsList.length})
          </div>
          {annotationsList.length > 0 && (
            <button
              onClick={handleClearAll}
              className="text-xs text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)] transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto">
          {annotationsList.length === 0 ? (
            <div className="p-4 text-center text-sm text-[color:var(--color-muted-foreground)]">
              {isEnabled
                ? "Select text on the page to create annotations"
                : "Enable annotations to get started"}
            </div>
          ) : (
            <ul className="divide-y divide-[--color-border]">
              {annotationsList.map((annotation) => {
                const config = COLORS[annotation.color];
                return (
                  <li
                    key={annotation.id}
                    className="group p-3 hover:bg-[--surface] transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-1 h-12 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: config.underline,
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm mb-1 line-clamp-2">
                          {annotation.target.quote}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[color:var(--color-muted-foreground)]">
                          <span className="capitalize">{annotation.style}</span>
                          <span>•</span>
                          <span>{config.name}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemove(annotation.id)}
                        className="text-xs text-[color:var(--color-muted-foreground)] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
